
import { EntityAdapter, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { Objectish, Patch, WritableDraft, applyPatches, castImmutable, enablePatches, original, produceWithPatches } from "immer";
import { z } from "zod";

import { FormLens, Path } from "@tdp/common/form";


// Enable immer patches so we can use them to create an overlay with only the changed fields rather than cloning the 
// entire object.
enablePatches();

function compressPatches(base: Objectish, patches: Patch[]): Patch[] {
  const [, compressedPatches] = produceWithPatches(base, (prevState) => applyPatches(prevState, patches));
  return compressedPatches;
}

//TODO(nibeauli): add a way to mount this state on a different key
export interface FormState {
  userModifications: Record<string, Patch[] | undefined>;
}
export type EntityFormState<T> = FormState & EntityState<T, string>;

export type FormLensData<T> = Omit<FormLens<T>, 'update' | 'clear'>;

/**
 * The entity form adapter is used for creating "temporary" form state for entities that are being edited.
 * 
 * This works by creating layers of the entity that are merged to form the "current" copy.
 * In order of reverse precedence:
 * 1. The base entity
 * 2. The entity in the store
 * 3. The user modifications
 * @param entityAdapter the base rtk entity adapter 
 * @returns the entity form adapter
 */
export function createEntityFormAdapter<T extends Objectish>(entityAdapter: EntityAdapter<T, string>) {
  const entitySelectors = entityAdapter.getSelectors();
  function getInitialState<TState extends object>(base: TState): EntityFormState<T> & TState {
    const initialState = {} as Record<string, Patch[] | undefined>;
    return {
      ...entityAdapter.getInitialState(base),
      userModifications: initialState
    }
  }
  function getSelectors(empty: T, schema: z.ZodType<T>) {
    return {
      getEntityFormById: (state: EntityFormState<T>, id: string): FormLensData<T> => {
        const entity = entitySelectors.selectById(state, id) as T | undefined;
        const current = applyPatches(entity ?? empty, state.userModifications[id] ?? []);
        return {
          current: castImmutable(current),
          base: castImmutable(entity ?? empty),
          validationErrors: schema.safeParse(current).error?.errors ?? [],
        }
      },
      getModifiedPathsById: (state: EntityFormState<T>, id: string): Path[] => {
        const patches = state.userModifications[id] ?? [];
        return patches.map(patch => patch.path);
      }
    };
  }

  function getReducers() {
    return {
      updateForm(state: WritableDraft<EntityFormState<T>>, action: PayloadAction<{ id: string, value: Patch[] }>) {
        //Explicit cast due to incompatibility of WritableDraft type... for read operations. 
        const entity = entitySelectors.selectById(state as EntityFormState<T>, action.payload.id) as T | undefined;
        const prevPatches = state.userModifications[action.payload.id];
        let nextPatches = [...(prevPatches ?? []), ...action.payload.value];

        // if we have a base object, we can use that to create a compressed patch set (this should almost always be true)
        // If we start seeing this take too much time, we can defer this to only execute when patch list is too long or similar heuristics
        if (entity) {
          nextPatches = compressPatches(entity, nextPatches);
        }

        state.userModifications[action.payload.id] = nextPatches;
      },
      clearForm(state: WritableDraft<EntityFormState<T>>, action: PayloadAction<{ id: string }>) {
        const { id } = action.payload;
        state.userModifications[id] = undefined;
      }
    }
  }

  return {
    getInitialState,
    getSelectors,
    getReducers,
  }
}
